import React from 'react'
import { useDispatch } from 'react-redux'

import NewOrderToast from '@/components-latest/ui/Toaster/NewOrderToast/NewOrderToast'
import {
  TIME_BEFORE_UNMOUNT,
} from '@/components-latest/ui/Toaster/Toaster.constants'
import { removeOrderIdFromPending } from '@/ducks/app/slices/app.slice'
import { INotification } from '@/ducks/notifications/slices/notification.slice'

import Styles from '../Toaster.module.scss'
import { HeightT } from '../Toaster.types'

interface IPiledToastProps {
  toast: INotification
  setHeights: React.Dispatch<React.SetStateAction<HeightT[]>>
  visibleToasts: number
  heights: HeightT[]
  index: number
  toasts: INotification[]
  expanded: boolean
  gap: number
  systemToastsLength: number
  shallow?: boolean
  onToastRemove: (toast: INotification) => void
}

const PiledToast: React.FC<IPiledToastProps> = props => {
  const {
    toast,
    setHeights,
    visibleToasts,
    heights,
    index,
    toasts,
    expanded,
    gap,
    systemToastsLength = 0,
    shallow,
    onToastRemove
  } = props

  const [mounted, setMounted] = React.useState(false)
  const [removed, setRemoved] = React.useState(false)
  const [offsetBeforeRemove, setOffsetBeforeRemove] = React.useState(0)
  const [initialHeight, setInitialHeight] = React.useState(0)
  const toastRef = React.useRef<HTMLLIElement>(null)
  const isFront = index === 0
  const isVisible = index + 1 <= visibleToasts

  // Height index is used to calculate the offset as it gets updated before the toast array, which means we can calculate the new layout faster.
  const heightIndex = React.useMemo(
    () => heights.findIndex(height => height.toastId === toast.id) || 0,
    [heights, toast.id]
  )

  const offset = React.useRef(0)

  const toastsHeightBefore = React.useMemo(() => {
    return heights.reduce((prev, curr, reducerIndex) => {
      // Calculate offset up until current  toast
      if (reducerIndex >= heightIndex) {
        return prev
      }

      return prev + curr.height
    }, 0)
  }, [heights, heightIndex])

  offset.current = React.useMemo(
    () => heightIndex * gap + toastsHeightBefore,
    [heightIndex, toastsHeightBefore]
  )

  React.useEffect(() => {
    // Trigger enter animation without using CSS animation
    setMounted(true)
  }, [])

  React.useLayoutEffect(() => {
    if (!mounted) return

    if (toastRef.current) {
      const toastNode = toastRef.current
      const originalHeight = toastNode.style.height
      toastNode.style.height = 'auto'
      const newHeight = toastNode.getBoundingClientRect().height
      toastNode.style.height = originalHeight

      setInitialHeight(newHeight)
      setHeights(heights => {
        const alreadyExists = heights.find(
          height => height.toastId === toast.id
        )

        if (!alreadyExists) {
          return [{ toastId: toast.id, height: newHeight }, ...heights]
        } else {
          return heights.map(height =>
            height.toastId === toast.id
              ? { ...height, height: newHeight }
              : height
          )
        }
      })
    }
  }, [mounted, setHeights, toast.id])

  React.useEffect(() => {
    if (shallow) return
    const toastNode = toastRef.current

    if (toastNode) {
      const height = toastNode.getBoundingClientRect().height

      // Add toast height tot heights array after the toast is mounted
      setInitialHeight(height)
      setHeights(h => [{ toastId: toast.id, height }, ...h])

      return () =>
        setHeights(h => h.filter(height => height.toastId !== toast.id))
    }
  }, [setHeights, shallow, toast.id])

  const dispatch = useDispatch()

  const deleteToast = React.useCallback(() => {
    setRemoved(true)
    setOffsetBeforeRemove(offset.current)
    setHeights(h => h.filter(height => height.toastId !== toast.id))

    setTimeout(() => {
      onToastRemove(toast)
    }, TIME_BEFORE_UNMOUNT)

    if (toast.orderDetails?.order_id) {
      dispatch(
        removeOrderIdFromPending({ orderId: toast.orderDetails.order_id })
      )
    }
  }, [toast, onToastRemove, setHeights, offset, dispatch])

  if (!toast.orderDetails) return null

  const toastContent = (
    <NewOrderToast
      order={toast.orderDetails}
      onTimerEnd={deleteToast}
      isAutoAccepted={toast.type === 'autoAcceptOrder'}
    />
  )

  return (
    <li
      data-testid="piled-toast"
      aria-atomic="true"
      role="status"
      tabIndex={0}
      ref={toastRef}
      className={Styles.piledToast}
      data-mounted={mounted}
      data-removed={removed}
      data-visible={isVisible}
      data-index={index}
      data-front={isFront}
      data-expanded={Boolean(expanded)}
      style={
        {
          '--index': index,
          '--toasts-before': index,
          '--z-index': toasts.length + systemToastsLength - index,
          '--offset': `${removed ? offsetBeforeRemove : offset.current}px`,
          '--initial-height': `${initialHeight}px`
        } as React.CSSProperties
      }>
      {toastContent}
    </li>
  )
}

export { PiledToast }
